import React from 'react';
import { ClipboardList, Loader2, X } from 'lucide-react';
import { briefService } from '../../services/briefService';
import { useToast } from '../UI/Toast';

export interface TrialBriefOption {
    id: string;
    title: string;
    budgetRange: string;
    trialCount: number;
}

export interface TrialRequestSubmitValue {
    brief: TrialBriefOption;
    trialFeeAmount?: number;
    reviewSlaHours?: number;
}

interface TrialRequestDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (value: TrialRequestSubmitValue) => void;
    briefOwnerUserId?: string;
    defaultBriefId?: string;
    requesterLabel: string;
    canSetPaidTrialAmount?: boolean;
}

export const TrialRequestDialog: React.FC<TrialRequestDialogProps> = ({
    isOpen,
    onClose,
    onSubmit,
    briefOwnerUserId,
    defaultBriefId,
    requesterLabel,
    canSetPaidTrialAmount = false
}) => {
    const { showToast } = useToast();
    const [briefs, setBriefs] = React.useState<TrialBriefOption[]>([]);
    const [selectedBriefId, setSelectedBriefId] = React.useState('');
    const [trialFeeAmountInput, setTrialFeeAmountInput] = React.useState('');
    const [reviewSlaHours, setReviewSlaHours] = React.useState<number>(120);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (!briefOwnerUserId) {
            setBriefs([]);
            setSelectedBriefId('');
            setTrialFeeAmountInput('');
            return;
        }

        let isActive = true;
        setLoading(true);

        briefService.getBriefs({ clientUserId: briefOwnerUserId, limit: 50 })
            .then((response) => {
                if (!isActive) {
                    return;
                }

                const options = (response.data?.briefs || []).map((brief) => ({
                    id: String(brief.id),
                    title: brief.title,
                    budgetRange: brief.budgetRange || '可谈',
                    trialCount: brief.trialCount || 0
                }));
                setBriefs(options);

                const initialSelection = options.find((brief) => brief.id === String(defaultBriefId))?.id || options[0]?.id || '';
                setSelectedBriefId(initialSelection);
                setTrialFeeAmountInput('');
            })
            .catch((error) => {
                console.error('Load trial briefs failed:', error);
                if (isActive) {
                    setBriefs([]);
                    setSelectedBriefId('');
                    setTrialFeeAmountInput('');
                    showToast('加载需求列表失败', 'error');
                }
            })
            .finally(() => {
                if (isActive) {
                    setLoading(false);
                }
            });

        return () => {
            isActive = false;
        };
    }, [defaultBriefId, briefOwnerUserId, isOpen, showToast]);

    if (!isOpen) {
        return null;
    }

    const selectedBrief = briefs.find((brief) => brief.id === selectedBriefId);

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-xl rounded-3xl bg-white shadow-2xl border border-gray-100 overflow-hidden animate-scale-in">
                <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between">
                    <div>
                        <h3 className="text-2xl font-bold text-gray-900">发起试稿</h3>
                        <p className="mt-1 text-sm text-gray-500">请选择一个{requesterLabel}，对方接受后试稿才会开始。</p>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-50 transition-colors">
                        <X className="w-6 h-6 text-gray-400" />
                    </button>
                </div>

                <div className="px-8 py-6">
                    {loading ? (
                        <div className="py-16 flex flex-col items-center justify-center text-gray-500 gap-3">
                            <Loader2 className="w-6 h-6 animate-spin" />
                            <span>正在加载需求列表...</span>
                        </div>
                    ) : briefs.length === 0 ? (
                        <div className="py-16 text-center text-gray-500">
                            暂无可用于发起试稿的需求
                        </div>
                    ) : (
                        <div className="space-y-5">
                            <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1">
                                {briefs.map((brief) => {
                                    const isSelected = brief.id === selectedBriefId;
                                    return (
                                        <button
                                            key={brief.id}
                                            type="button"
                                            onClick={() => setSelectedBriefId(brief.id)}
                                            className={`w-full text-left rounded-2xl border px-5 py-4 transition-all ${
                                                isSelected
                                                    ? 'border-black bg-gray-50 shadow-sm'
                                                    : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                                            }`}
                                        >
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <ClipboardList className={`w-4 h-4 ${isSelected ? 'text-black' : 'text-gray-400'}`} />
                                                        <span className="font-semibold text-gray-900 truncate">{brief.title}</span>
                                                    </div>
                                                    <div className="mt-2 flex items-center gap-3 text-sm text-gray-500">
                                                        <span>预算: {brief.budgetRange}</span>
                                                        <span>试稿次数: {brief.trialCount}</span>
                                                    </div>
                                                </div>
                                                {isSelected && (
                                                    <span className="inline-flex h-6 items-center rounded-full bg-black px-2.5 text-xs font-medium text-white">
                                                        已选择
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            {canSetPaidTrialAmount && (
                                <div className="rounded-2xl border border-gray-100 bg-gray-50 px-5 py-4">
                                    <label className="block text-sm font-semibold text-gray-900 mb-2">付费金额</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={trialFeeAmountInput}
                                        onChange={(event) => setTrialFeeAmountInput(event.target.value)}
                                        placeholder="选填，不填则视为免费"
                                        className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                                    />
                                    <p className="mt-2 text-xs text-gray-500">创作者接受试稿后，需求方可在试稿详情页支付该金额。</p>
                                </div>
                            )}

                            <div className="rounded-2xl border border-gray-100 bg-gray-50 px-5 py-4">
                                <label className="block text-sm font-semibold text-gray-900 mb-2">客户审核期限</label>
                                <p className="mb-3 text-xs text-gray-500">创作者提交试稿后，您需要在此期限内完成评估，否则系统将自动通过。</p>
                                <div className="flex gap-2">
                                    {[72, 120, 168].map((h) => {
                                        const active = reviewSlaHours === h;
                                        return (
                                            <button
                                                key={h}
                                                type="button"
                                                onClick={() => setReviewSlaHours(h)}
                                                className={`px-4 py-1.5 text-sm rounded-full border transition-colors ${
                                                    active
                                                        ? 'bg-black text-white border-black'
                                                        : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
                                                }`}
                                            >
                                                {h / 24} 天
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="px-8 py-5 border-t border-gray-100 flex justify-end gap-3 bg-gray-50/60">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-white transition-colors"
                    >
                        取消
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            if (!selectedBrief) {
                                return;
                            }

                            const normalizedAmount = trialFeeAmountInput.trim() === ''
                                ? undefined
                                : Number(trialFeeAmountInput);

                            if (normalizedAmount !== undefined && (!Number.isFinite(normalizedAmount) || normalizedAmount < 0)) {
                                showToast('请输入有效的非负试稿金额', 'error');
                                return;
                            }

                            onSubmit({
                                brief: selectedBrief,
                                trialFeeAmount: normalizedAmount && normalizedAmount > 0
                                    ? Math.round(normalizedAmount * 100) / 100
                                    : undefined,
                                reviewSlaHours,
                            });
                        }}
                        disabled={!selectedBrief}
                        className="px-7 py-2.5 rounded-xl bg-black text-white font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        发起试稿
                    </button>
                </div>
            </div>
        </div>
    );
};
